import { useTranslation } from '@hooks/useTranslation';
import { DateFilterPreset, ExecutionFilterState, ProcessExecutionStatus, SelectedProcess, SelectedService, SelectedUser } from '@models/processExecution.model';
import { Close } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import WolfButton from '@components/ui/WolfButton';
import { graphQlClient } from '@config/graphqlClient';
import UserGraphQL from '@graphql/user.queries';

interface UserOption {
  firstName: string;
  lastName: string;
  email: string;
}

interface ProcessOption {
  processId: string;
  name: string;
}

interface ServiceOption {
  serviceId: string;
  name: string;
  areaId?: string;
  areaName?: string;
  count?: number;
}

interface StatusOption {
  value: ProcessExecutionStatus;
  label: string;
}

interface getOrganizationUsersEmailRequest {
  getOrganizationUsersEmail: UserOption[];
}

interface AreaWithServices {
  areaId: string;
  name: string;
  color: string;
  services: ServiceOption[];
}

interface ExecutionsFilterToolbarProps {
  filterState: ExecutionFilterState;
  onFilterChange: (newState: ExecutionFilterState) => void;
  availableProcesses: ProcessOption[];
  availableAreas: AreaWithServices[];
  availableServices: ServiceOption[];
  dateFilterOnly?: boolean;
  dateFilterFirst?: boolean;
}

const ExecutionsFilterToolbar: React.FC<ExecutionsFilterToolbarProps> = ({
  filterState,
  onFilterChange,
  availableProcesses,
  availableAreas,
  availableServices,
  dateFilterOnly = false,
  dateFilterFirst = false,
}) => {
  const localeProcesses = useTranslation('processes');

  const [users, setUsers] = useState<UserOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const [selectedProcesses, setSelectedProcesses] = useState<ProcessOption[]>([]);
  const [selectedServices, setSelectedServices] = useState<ServiceOption[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<StatusOption[]>([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Sync selectedUsers with filterState
  useEffect(() => {
    if (filterState.selectedUsers.length === 0) {
      setSelectedUsers([]);
    }
  }, [filterState.selectedUsers]);

  // Sync selectedProcesses with filterState
  useEffect(() => {
    if (filterState.selectedProcesses.length === 0) {
      setSelectedProcesses([]);
    }
  }, [filterState.selectedProcesses]);

  // Sync selectedServices with filterState
  useEffect(() => {
    if (filterState.selectedServices.length === 0) {
      setSelectedServices([]);
    }
  }, [filterState.selectedServices]);

  // Sync selectedStatuses with filterState
  useEffect(() => {
    if (filterState.selectedStatuses.length === 0) {
      setSelectedStatuses([]);
    }
  }, [filterState.selectedStatuses]);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response: getOrganizationUsersEmailRequest = await graphQlClient.request(
        UserGraphQL.queries.getOrganizationUsersEmail,
      );
      if (response.getOrganizationUsersEmail) {
        setUsers(response.getOrganizationUsersEmail);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUsersSelect = (_event: React.SyntheticEvent, newUsers: UserOption[]) => {
    setSelectedUsers(newUsers);
    const mappedUsers: SelectedUser[] = newUsers.map(user => ({
      email: user.email,
      displayName: getUserDisplayName(user),
    }));
    onFilterChange({
      ...filterState,
      selectedUsers: mappedUsers,
    });
  };

  const handleProcessesSelect = (_event: React.SyntheticEvent, newProcesses: ProcessOption[]) => {
    setSelectedProcesses(newProcesses);
    const mappedProcesses: SelectedProcess[] = newProcesses.map(process => ({
      processId: process.processId,
      name: process.name,
    }));
    onFilterChange({
      ...filterState,
      selectedProcesses: mappedProcesses,
    });
  };

  const handleRemoveUser = (emailToRemove: string) => {
    const newSelectedUsers = selectedUsers.filter(u => u.email !== emailToRemove);
    setSelectedUsers(newSelectedUsers);
    onFilterChange({
      ...filterState,
      selectedUsers: filterState.selectedUsers.filter(u => u.email !== emailToRemove),
    });
  };

  const handleRemoveProcess = (processIdToRemove: string) => {
    const newSelectedProcesses = selectedProcesses.filter(p => p.processId !== processIdToRemove);
    setSelectedProcesses(newSelectedProcesses);
    onFilterChange({
      ...filterState,
      selectedProcesses: filterState.selectedProcesses.filter(p => p.processId !== processIdToRemove),
    });
  };

  const handleAreaClick = (area: AreaWithServices) => {
    // Add all services from this area to selected services
    const areaServiceIds = area.services.map(s => s.serviceId);
    const currentServiceIds = selectedServices.map(s => s.serviceId);

    // Check if all services from this area are already selected
    const allSelected = areaServiceIds.every(id => currentServiceIds.includes(id));

    let newSelectedServices: ServiceOption[];
    if (allSelected) {
      // Remove all services from this area
      newSelectedServices = selectedServices.filter(s => !areaServiceIds.includes(s.serviceId));
    } else {
      // Add all services from this area that aren't already selected
      const servicesToAdd = area.services.filter(s => !currentServiceIds.includes(s.serviceId));
      newSelectedServices = [...selectedServices, ...servicesToAdd];
    }

    setSelectedServices(newSelectedServices);
    const mappedServices: SelectedService[] = newSelectedServices.map(service => ({
      serviceId: service.serviceId,
      name: service.name,
    }));
    onFilterChange({
      ...filterState,
      selectedServices: mappedServices,
    });
  };

  const handleServicesSelect = (_event: React.SyntheticEvent, newServices: ServiceOption[]) => {
    setSelectedServices(newServices);
    const mappedServices: SelectedService[] = newServices.map(service => ({
      serviceId: service.serviceId,
      name: service.name,
    }));
    onFilterChange({
      ...filterState,
      selectedServices: mappedServices,
    });
  };

  const handleRemoveService = (serviceIdToRemove: string) => {
    const newSelectedServices = selectedServices.filter(s => s.serviceId !== serviceIdToRemove);
    setSelectedServices(newSelectedServices);
    onFilterChange({
      ...filterState,
      selectedServices: filterState.selectedServices.filter(s => s.serviceId !== serviceIdToRemove),
    });
  };

  const handleRemoveStatus = (statusToRemove: ProcessExecutionStatus) => {
    const newSelectedStatuses = selectedStatuses.filter(s => s.value !== statusToRemove);
    setSelectedStatuses(newSelectedStatuses);
    onFilterChange({
      ...filterState,
      selectedStatuses: filterState.selectedStatuses.filter(s => s !== statusToRemove),
    });
  };

  const handleDateFilterChange = (preset: DateFilterPreset | undefined) => {
    onFilterChange({
      ...filterState,
      dateFilter: preset,
      // Clear custom dates if not custom preset
      customDateFrom: preset === 'custom' ? filterState.customDateFrom : undefined,
      customDateTo: preset === 'custom' ? filterState.customDateTo : undefined,
    });
  };

  const handleCustomDateChange = (field: 'from' | 'to', value: string) => {
    onFilterChange({
      ...filterState,
      dateFilter: 'custom',
      customDateFrom: field === 'from' ? value : filterState.customDateFrom,
      customDateTo: field === 'to' ? value : filterState.customDateTo,
    });
  };

  const handleClearFilters = () => {
    setSelectedUsers([]);
    setSelectedProcesses([]);
    setSelectedServices([]);
    setSelectedStatuses([]);
    onFilterChange({
      ...filterState,
      selectedUsers: [],
      selectedProcesses: [],
      selectedAreas: [],
      selectedServices: [],
      selectedStatuses: [],
    });
  };

  const getUserDisplayName = (user: UserOption) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return user.email;
  };

  const datePresets: { value: DateFilterPreset; label: string }[] = [
    { value: 'today', label: localeProcesses['today'] },
    { value: 'week', label: localeProcesses['last7Days'] },
    { value: 'month', label: localeProcesses['last30Days'] },
    { value: 'custom', label: localeProcesses['customRange'] },
  ];

  const statusOptions: StatusOption[] = [
    { value: ProcessExecutionStatus.ASSIGNED, label: localeProcesses['assigned'] },
    { value: ProcessExecutionStatus.IN_PROGRESS, label: localeProcesses['in_progress'] },
    { value: ProcessExecutionStatus.COMPLETED, label: localeProcesses['completed'] },
    { value: ProcessExecutionStatus.FAILED, label: localeProcesses['failed'] },
    { value: ProcessExecutionStatus.CANCELLED, label: localeProcesses['cancelled'] },
    { value: ProcessExecutionStatus.SKIPPED, label: localeProcesses['skipped'] },
  ];

  const handleStatusesSelect = (_event: React.SyntheticEvent, newStatuses: StatusOption[]) => {
    setSelectedStatuses(newStatuses);
    const mappedStatuses: ProcessExecutionStatus[] = newStatuses.map(s => s.value);
    onFilterChange({
      ...filterState,
      selectedStatuses: mappedStatuses,
    });
  };

  const isAreaFullySelected = (area: AreaWithServices): boolean => {
    const areaServiceIds = area.services.map(s => s.serviceId);
    const currentServiceIds = selectedServices.map(s => s.serviceId);
    return areaServiceIds.length > 0 && areaServiceIds.every(id => currentServiceIds.includes(id));
  };

  const hasActiveFilters = filterState.selectedUsers.length > 0 ||
    filterState.selectedProcesses.length > 0 ||
    filterState.selectedAreas.length > 0 ||
    filterState.selectedServices.length > 0 ||
    (filterState.selectedStatuses && filterState.selectedStatuses.length > 0) ||
    (filterState.dateFilter !== 'today' && filterState.dateFilter !== undefined);

  const renderDateFilter = () => (
    <Box className="flex flex-wrap items-end gap-2 mb-2">
      <Box>
        <Box className="flex flex-wrap items-center gap-2">
          {datePresets.map((preset) => (
            <Chip
              key={preset.value}
              label={preset.label}
              onClick={() => handleDateFilterChange(
                filterState.dateFilter === preset.value ? undefined : preset.value
              )}
              color={filterState.dateFilter === preset.value ? 'primary' : 'default'}
              variant={filterState.dateFilter === preset.value ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
          {/* Custom date inputs - inline */}
          {filterState.dateFilter === 'custom' && (
            <>
              <TextField
                type="date"
                size="small"
                value={filterState.customDateFrom || ''}
                onChange={(e) => handleCustomDateChange('from', e.target.value)}
                sx={{ width: 140 }}
              />
              <Typography variant="body2" className="text-textSecondary">-</Typography>
              <TextField
                type="date"
                size="small"
                value={filterState.customDateTo || ''}
                onChange={(e) => handleCustomDateChange('to', e.target.value)}
                sx={{ width: 140 }}
              />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderDropdownFilters = () => (
    <Box className="flex flex-wrap items-end gap-2 mb-2">
      {/* Status filter */}
      <Box sx={{ width: 200 }}>
        <Typography variant="body2" className="mb-1 text-textSecondary">
          {localeProcesses['filterByStatus']}
        </Typography>
        <Autocomplete
          multiple
          value={selectedStatuses}
          onChange={handleStatusesSelect}
          options={statusOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.value}>
              <Typography variant="body2">{option.label}</Typography>
            </Box>
          )}
          renderTags={() => null}
          renderInput={(params) => {
            let placeholder = localeProcesses['filterByStatus'];
            if (filterState.selectedStatuses.length === 1) {
              const status = statusOptions.find(s => s.value === filterState.selectedStatuses[0]);
              placeholder = status?.label || placeholder;
            } else if (filterState.selectedStatuses.length > 1) {
              placeholder = localeProcesses['multiple'];
            }
            return (
              <TextField
                {...params}
                placeholder={placeholder}
                size="small"
              />
            );
          }}
        />
      </Box>

      {/* User filter */}
      <Box sx={{ width: 200 }}>
        <Typography variant="body2" className="mb-1 text-textSecondary">
          {localeProcesses['filterByUser']}
        </Typography>
        <Autocomplete
          multiple
          value={selectedUsers}
          onChange={handleUsersSelect}
          options={users}
          loading={usersLoading}
          getOptionLabel={(option) => getUserDisplayName(option)}
          isOptionEqualToValue={(option, value) => option.email === value.email}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.email}>
              <Box>
                <Typography variant="body2">{getUserDisplayName(option)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.email}
                </Typography>
              </Box>
            </Box>
          )}
          renderTags={() => null}
          renderInput={(params) => {
            let placeholder = localeProcesses['searchByUser'];
            if (filterState.selectedUsers.length === 1) {
              placeholder = filterState.selectedUsers[0].displayName;
            } else if (filterState.selectedUsers.length > 1) {
              placeholder = `${filterState.selectedUsers.length} ${localeProcesses['users'].toLowerCase()}`;
            }
            return (
              <TextField
                {...params}
                placeholder={placeholder}
                size="small"
              />
            );
          }}
        />
      </Box>

      {/* Process filter */}
      <Box sx={{ width: 200 }}>
        <Typography variant="body2" className="mb-1 text-textSecondary">
          {localeProcesses['filterByProcess']}
        </Typography>
        <Autocomplete
          multiple
          value={selectedProcesses}
          onChange={handleProcessesSelect}
          options={availableProcesses}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.processId === value.processId}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.processId}>
              <Typography variant="body2">{option.name}</Typography>
            </Box>
          )}
          renderTags={() => null}
          renderInput={(params) => {
            let placeholder = localeProcesses['searchByProcess'];
            if (filterState.selectedProcesses.length === 1) {
              placeholder = filterState.selectedProcesses[0].name;
            } else if (filterState.selectedProcesses.length > 1) {
              placeholder = `${filterState.selectedProcesses.length} ${localeProcesses['processes'].toLowerCase()}`;
            }
            return (
              <TextField
                {...params}
                placeholder={placeholder}
                size="small"
              />
            );
          }}
        />
      </Box>

      {/* Service filter (grouped by area) */}
      <Box sx={{ width: 250 }}>
        <Typography variant="body2" className="mb-1 text-textSecondary">
          {localeProcesses['filterByService']}
        </Typography>
        <Autocomplete
          multiple
          value={selectedServices}
          onChange={handleServicesSelect}
          options={availableServices}
          groupBy={(option) => option.areaName || 'Other'}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.serviceId === value.serviceId}
          renderGroup={(params) => {
            const area = availableAreas.find(a => a.name === params.group);
            const isFullySelected = area ? isAreaFullySelected(area) : false;

            return (
              <li key={params.key}>
                <Box
                  sx={{
                    position: 'sticky',
                    top: '-8px',
                    padding: '4px 10px',
                    backgroundColor: '#f9f9f9',
                    borderLeft: `4px solid ${area?.color || '#cccccc'}`,
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '&:hover': {
                      backgroundColor: '#f0f0f0',
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (area) handleAreaClick(area);
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: area?.color || '#cccccc',
                      }}
                    />
                    <span>{params.group}</span>
                  </Box>
                  {isFullySelected && <span style={{ fontSize: '0.75rem', color: '#666' }}>✓ All</span>}
                </Box>
                <ul style={{ padding: 0 }}>{params.children}</ul>
              </li>
            );
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.serviceId}>
              <Typography variant="body2">
                {option.name} {option.count !== undefined && option.count > 0 && (
                  <span style={{ color: '#999', fontSize: '0.875rem' }}>({option.count})</span>
                )}
              </Typography>
            </Box>
          )}
          renderTags={() => null}
          renderInput={(params) => {
            let placeholder = localeProcesses['searchByService'];
            if (filterState.selectedServices.length === 1) {
              placeholder = filterState.selectedServices[0].name;
            } else if (filterState.selectedServices.length > 1) {
              placeholder = `${filterState.selectedServices.length} ${localeProcesses['services'].toLowerCase()}`;
            }
            return (
              <TextField
                {...params}
                placeholder={placeholder}
                size="small"
              />
            );
          }}
        />
      </Box>
    </Box>
  );

  return (
    <Box className="mb-2">
      {/* Date filter first if dateFilterFirst is true */}
      {dateFilterFirst && renderDateFilter()}

      {/* Dropdown filters */}
      {!dateFilterOnly && renderDropdownFilters()}

      {/* Date filter (if not first) */}
      {!dateFilterFirst && renderDateFilter()}

      {/* Selected filters chips */}
      {!dateFilterOnly && (filterState.selectedUsers.length > 0 || filterState.selectedProcesses.length > 0 ||
        filterState.selectedServices.length > 0 || (filterState.selectedStatuses && filterState.selectedStatuses.length > 0)) && (
        <Box className="mb-4">
          {/* Filters title and clear button */}
          <Box className="flex items-center gap-2 mb-2">
            <Typography variant="subtitle2" className="font-semibold">
              {localeProcesses['filters']}:
            </Typography>
            {hasActiveFilters && (
              <WolfButton color="tertiary" onClick={handleClearFilters} size="small">
                {localeProcesses['clearFilters']}
              </WolfButton>
            )}
          </Box>

          {/* Filter chips grouped by type */}
          <Box className="flex flex-col gap-2">
            {filterState.selectedUsers.length > 0 && (
              <Box className="flex flex-wrap items-center gap-2">
                <Typography variant="body2" color="text.secondary">
                  {localeProcesses['users']}:
                </Typography>
                {filterState.selectedUsers.map((user) => (
                  <Chip
                    key={user.email}
                    label={user.displayName}
                    onDelete={() => handleRemoveUser(user.email)}
                    deleteIcon={<Close />}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            )}
            {filterState.selectedProcesses.length > 0 && (
              <Box className="flex flex-wrap items-center gap-2">
                <Typography variant="body2" color="text.secondary">
                  {localeProcesses['processes']}:
                </Typography>
                {filterState.selectedProcesses.map((process) => (
                  <Chip
                    key={process.processId}
                    label={process.name}
                    onDelete={() => handleRemoveProcess(process.processId)}
                    deleteIcon={<Close />}
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            )}
            {filterState.selectedServices.length > 0 && (
              <Box className="flex flex-wrap items-center gap-2">
                <Typography variant="body2" color="text.secondary">
                  {localeProcesses['services']}:
                </Typography>
                {filterState.selectedServices.map((service) => (
                  <Chip
                    key={service.serviceId}
                    label={service.name}
                    onDelete={() => handleRemoveService(service.serviceId)}
                    deleteIcon={<Close />}
                    color="default"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            )}
            {filterState.selectedStatuses && filterState.selectedStatuses.length > 0 && (
              <Box className="flex flex-wrap items-center gap-2">
                <Typography variant="body2" color="text.secondary">
                  {localeProcesses['statuses']}:
                </Typography>
                {filterState.selectedStatuses.map((status) => {
                  const statusOption = statusOptions.find(s => s.value === status);
                  return (
                    <Chip
                      key={status}
                      label={statusOption?.label || status}
                      onDelete={() => handleRemoveStatus(status)}
                      deleteIcon={<Close />}
                      color="default"
                      variant="outlined"
                      size="small"
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ExecutionsFilterToolbar;
